import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Link } from "@mui/material";
import { LoginForm } from "../components/LoginForm";
import { ConsentPopup } from "../components/ConsentPopup";
import { ConsentDeclinedMessage } from "../components/ConsentDeclinedMessage";
import { useUpdateConsent, useSessionQuery } from "../";
import { ROUTES } from "../../../shared/utils";
import { qk } from "../../../lib";
import { Session } from "../api";

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showConsentPopup, setShowConsentPopup] = useState(false);
  const [showDeclinedMessage, setShowDeclinedMessage] = useState(false);
  const updateConsentMutation = useUpdateConsent();
  const { data: session } = useSessionQuery();

  useEffect(() => {
    if (session?.authenticated) {
      if (session.consent_declined) {
        setShowDeclinedMessage(true);
      } else if (session.needs_consent) {
        setShowConsentPopup(true);
      }
    }
  }, [session]);

  const handleLoginSuccess = (data?: { consent?: unknown; is_admin?: boolean }) => {
    const consent = data?.consent as { decision?: string } | null | undefined;
    const isAdmin = data?.is_admin ?? false;

    if (!consent) {
      setShowConsentPopup(true);
    } else if (consent.decision === "decline") {
      setShowDeclinedMessage(true);
    } else {
      if (isAdmin) {
        navigate(ROUTES.ADMIN_DASHBOARD);
      } else {
        navigate("/");
      }
    }
  };

  const handleConsentSubmit = async (formData: {
    privacyPolicyAcknowledged: boolean;
    option: "consent" | "decline";
    confirmationText: string;
  }) => {
    await updateConsentMutation.mutateAsync({
      privacyPolicyAcknowledged: formData.privacyPolicyAcknowledged,
      decision: formData.option,
      confirmationText: formData.confirmationText,
    });
    setShowConsentPopup(false);

    await queryClient.refetchQueries({ queryKey: qk.me() });
    const sessionData = queryClient.getQueryData<Session>(qk.me());

    if (sessionData?.is_admin) {
      navigate(ROUTES.ADMIN_DASHBOARD);
    } else {
      navigate("/");
    }
  };

  const handleBackToLogin = () => {
    setShowDeclinedMessage(false);
    navigate("/");
  };

  const handleConsentChanged = () => {
    setShowDeclinedMessage(false);
    navigate("/");
  };

  if (showDeclinedMessage) {
    return (
      <ConsentDeclinedMessage
        onBackToLogin={handleBackToLogin}
        onConsentChanged={handleConsentChanged}
      />
    );
  }

  return (
    <>
      <LoginForm onSuccess={handleLoginSuccess} />
      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Link component={RouterLink} to={ROUTES.FAQ} underline="hover">
          FAQ
        </Link>
      </Box>
      <ConsentPopup
        open={showConsentPopup}
        onClose={() => setShowConsentPopup(false)}
        onSubmit={handleConsentSubmit}
      />
    </>
  );
};
